import { useRef } from 'react';

import {
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';
import { CloseCircleOutline } from '@aslanpay/portal-common-lib';

import Step from 'app/components/UI/Step';

import useStep from 'app/hooks/useStep';

import { useCreatePayCalendarStore } from 'app/store';

import {
  PayCalendarDefineCalendarForm,
  PayCalendarPaydayInformationForm,
  PayCalendarCalendarDefaultsForm,
  PayCalendarCutOffInformationForm,
  PayCalendarAslanDetailsForm,
} from '../CreatePayCalendarForm';

import { CreatePayCalendarModalProps } from './types';

function CreatePayCalendarModal({ isOpen, onClose, onCreate }: CreatePayCalendarModalProps) {
  const resetPayCalendarData = useCreatePayCalendarStore((state) => state.resetPayCalendarData);

  const submitButtonRef = useRef<HTMLElement>(null);

  const steps = [
    {
      id: 'pay-calendar-define-calendar-form',
      name: 'Define calendar',
    },
    {
      id: 'pay-calendar-payday-information-form',
      name: 'Payday information',
    },
    {
      id: 'pay-calendar-calendar-defaults-form',
      name: 'Calendar defaults',
    },
    {
      id: 'pay-calendar-cutoff-information-form',
      name: 'Cut-off information',
    },
    {
      id: 'pay-calendar-aslan-details-form',
      name: 'Aslan details',
    },
  ];

  const { currentStep, nextStep, previousStep, canPrevStep, resetSteps } = useStep(steps.length);

  const handleClose = () => {
    resetPayCalendarData();
    resetSteps();
    onClose();
  };

  const handleCreate = () => {
    resetSteps();
    onClose();
    onCreate();
  };

  const renderCurrentStepForm = () => {
    switch (steps[currentStep].id) {
      case 'pay-calendar-payday-information-form':
        return (
          <PayCalendarPaydayInformationForm
            id={steps[currentStep].id}
            ref={submitButtonRef}
            onStepAction={nextStep}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
      case 'pay-calendar-calendar-defaults-form':
        return (
          <PayCalendarCalendarDefaultsForm
            id={steps[currentStep].id}
            ref={submitButtonRef}
            onStepAction={nextStep}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );

      case 'pay-calendar-cutoff-information-form':
        return (
          <PayCalendarCutOffInformationForm
            id={steps[currentStep].id}
            ref={submitButtonRef}
            onStepAction={nextStep}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
      case 'pay-calendar-aslan-details-form':
        return (
          <PayCalendarAslanDetailsForm
            id={steps[currentStep].id}
            ref={submitButtonRef}
            onStepAction={handleCreate}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
      case 'pay-calendar-define-calendar-form':
      default:
        return (
          <PayCalendarDefineCalendarForm
            id={steps[currentStep].id}
            ref={submitButtonRef}
            onStepAction={nextStep}
            previousStep={previousStep}
            canPrevStep={canPrevStep}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={handleClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Create a new pay calendar</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close create pay calendar"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Stack spacing="40px">
            <HStack spacing="0" flexGrow={1} alignItems="flex-start">
              {steps.map((step, id) => (
                <Step
                  key={step.name}
                  number={id + 1}
                  name={step.name}
                  isActive={currentStep === id}
                  isCompleted={currentStep > id}
                  isFirstStep={id === 0}
                  isLastStep={steps.length === id + 1}
                />
              ))}
            </HStack>
            {renderCurrentStepForm()}
          </Stack>
        </ModalBody>
        <ModalFooter ref={submitButtonRef}>
          <Text color="system.error" textStyle="extraSmallMedium">
            * mandatory
          </Text>
          <Spacer />
          <Button mr="20px" variant="link" size="sm" onClick={handleClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CreatePayCalendarModal;
